import router from '../../../router';
import { Toast } from '../Toast';

const BASE_URL = process.env.NODE_ENV === 'production'
    ? '/api/'
    : 'http://localhost:3000/api/'


import axios from 'axios'
axios.defaults.withCredentials = true;

function showErrorToast(message, silent) {
    if (silent) return;
    new Toast({
        message,
        type: 'error',
        duration: 5000,
    });
}

/** Screens that handle a failure themselves need the HTTP status, including 5xx. */
function errorWithStatus(message, status) {
    const error = new Error(message);
    error.status = status;
    return error;
}

/** Prefer a human-readable string; never return a raw object (avoids "[object Object]" toasts). */
function extractErrorMessage(data, fallback) {
    if (typeof data === 'string' && data) return data;
    if (typeof data?.error === 'string' && data.error) return data.error;
    if (typeof data?.message === 'string' && data.message) return data.message;
    if (typeof data?.error?.message === 'string' && data.error.message) return data.error.message;
    return fallback || 'An error occurred';
}

async function ajax(endpoint, method='get', data=null, query=null, options = {}) {
    const silent = !!options.silent;
    try {
        const res = await axios({
            url: BASE_URL + endpoint,
            method,
            data,
            params: query,
            headers: method === 'get' ? { 'Cache-Control': 'no-cache' } : undefined,
        })
        return res.data;
    } catch (err) {
        // Handle specific HTTP status codes
        if (err.response) {
            const status = err.response.status;
            
            // 401 Unauthorized - redirect to login
            if (status === 401) {
                router.push('/');
                return;
            }
            
            // 503 Service Unavailable - backend server down
            if (status === 503) {
                showErrorToast('Server is temporarily unavailable. Please try again in a few moments.', silent);
                throw errorWithStatus('Service temporarily unavailable (503)', status);
            }
            
            // 500 Internal Server Error
            if (status === 500) {
                showErrorToast('Server error occurred. Please try again or contact support.', silent);
                throw errorWithStatus('Internal server error (500)', status);
            }
            
            // 502 Bad Gateway
            if (status === 502) {
                showErrorToast('Connection to server failed. Please check your internet connection.', silent);
                throw errorWithStatus('Bad gateway (502)', status);
            }
            
            // 504 Gateway Timeout
            if (status === 504) {
                showErrorToast('Request timed out. The server is taking too long to respond.', silent);
                throw errorWithStatus('Gateway timeout (504)', status);
            }
            
            // Structured validation errors — UI shows a specific message.
            // message stays human-readable so a screen that does not know the code still shows a sentence.
            if (status >= 400 && status < 500 && err.response?.data?.code) {
                const data = err.response.data;
                const e = new Error(typeof data.error === 'string' && data.error ? data.error : data.code);
                e.validationCode = data.code;
                e.validationDetails = data.details || {};
                e.status = status;
                throw e;
            }

            // Other errors with response (400, 403, 404, etc.)
            const errorMessage = extractErrorMessage(err.response.data, err.message);
            showErrorToast(errorMessage, silent);
            const e = new Error(errorMessage);
            e.status = status;
            throw e;
        }
        
        // Network error (no response from server)
        if (err.request) {
            showErrorToast('Cannot connect to server. Please check your internet connection.', silent);
            throw new Error('Network error: Unable to reach server');
        }
        
        // Other errors
        const errorMessage = err.message || 'An error occurred';
        showErrorToast(errorMessage, silent);
        throw new Error(errorMessage);
    }
}

export default {
    get(endpoint, data, query, options){
        return ajax(endpoint, 'GET', data, query, options)
    },
    post(endpoint, data, options){
        return ajax(endpoint, 'POST', data, null, options)
    },
    put(endpoint, data, options){
        return ajax(endpoint, 'PUT', data, null, options)
    },
    patch(endpoint, data, options){
        return ajax(endpoint, 'PATCH', data, null, options)
    },
    delete(endpoint, data, options){
        return ajax(endpoint, 'DELETE', data, null, options)
    }

}